// @ts-check
// Barras verticais e horizontais, agrupadas ou empilhadas, em SVG próprio (doc 16, 10.5): eixo de valores com
// grade tracejada leve, ponta arredondada de 4 px, rótulos de dados opcionais e tooltip por categoria. Cada barra com
// registros abre a lista da categoria e da série; a faixa da categoria, fora das barras, abre a categoria inteira
// (contrato 07, seção 10).
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import {
  ChartLegend,
  ChartTooltip,
  buildBarPath,
  buildNiceScale,
  areAllIntegers,
  estimateTextWidth,
  getSeriesColor,
  getSingleSeriesColor,
  truncateToWidth,
  useElementSize,
} from './chart-utils.js';
import { formatMetricValue } from './widget-format.js';
import { ChartBandLink, handleDrillLinkClick, useChartPointNavigation, useDrilldown } from './drilldown.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/dashboards.js').SeriesWidgetData} SeriesWidgetData
 * @typedef {import('../../data/dashboards.js').ChartSeries} ChartSeries
 * @typedef {import('./drilldown.js').ChartPoint & { label: string, valueText: string }} BarPoint
 * @typedef {{ index: number, x: number, y: number, source?: 'pointer' | 'keyboard' | 'touch', href?: string | null }} BarHover
 */

const MAX_BAR_THICKNESS = 32;
const BAR_GAP = 2;
const LABEL_ROTATION_DEGREES = 40;

/**
 * Séries com o formato principal vão para o gráfico; as demais (ex.: valor em reais no funil de alunos)
 * aparecem só no tooltip, para nunca haver dois eixos de escalas diferentes.
 * @param {SeriesWidgetData} data
 */
export function splitPlottedSeries(data) {
  const plotted = data.series.filter((series) => (series.format ?? data.format) === data.format);
  const extras = data.series.filter((series) => !plotted.includes(series));
  return { plotted, extras };
}

/**
 * @param {{ data: SeriesWidgetData, layout?: 'VERTICAL' | 'HORIZONTAL', stacked?: boolean, showDataLabels?: boolean, colorName?: string | null }} props
 */
export function BarChart({ data, layout = 'VERTICAL', stacked = false, showDataLabels = false, colorName }) {
  const [containerRef, size] = useElementSize();
  const [hover, setHover] = useState(/** @type {BarHover | null} */ (null));
  const drill = useDrilldown();
  const { plotted, extras } = splitPlottedSeries(data);
  const isStacked = stacked && plotted.length > 1;
  const colors = plotted.length === 1 && !plotted[0].color
    ? [getSingleSeriesColor(colorName)]
    : plotted.map((series, index) => (series.color ? `var(--report-series-${series.color}, ${getSingleSeriesColor(series.color)})` : getSeriesColor(index)));
  const categories = data.categories;
  const valueAt = (/** @type {ChartSeries} */ series, /** @type {number} */ index) => Number(series.values[index] ?? 0) || 0;
  const totals = categories.map((_, index) => plotted.reduce((sum, series) => sum + Math.max(0, valueAt(series, index)), 0));
  const allValues = plotted.flatMap((series) => categories.map((_, index) => valueAt(series, index)));
  const maximum = isStacked ? Math.max(0, ...totals) : Math.max(0, ...allValues);
  const minimum = Math.min(0, ...allValues);
  const format = data.format;
  const isHorizontal = layout === 'HORIZONTAL';
  const integer = format === 'NUMBER' && areAllIntegers(allValues);
  const width = size.width;

  // pontos clicáveis: cada barra (categoria x série desenhada) com registros para este papel
  /** @type {BarPoint[]} */
  const barPoints = [];
  if (drill) {
    categories.forEach((category, categoryIndex) => plotted.forEach((series, seriesIndex) => {
      const target = drill.target({ part: 'category', key: category.key, seriesKey: series.key });
      if (!target) return;
      barPoints.push({
        id: `${categoryIndex}:${seriesIndex}`, categoryIndex, seriesIndex, href: target.href,
        label: plotted.length > 1 ? `${category.label} · ${series.label}` : category.label,
        valueText: formatMetricValue(series.values[categoryIndex], format),
      });
    }));
  }
  const pointById = new Map(barPoints.map((point) => [point.id, point]));
  // faixa da categoria: a série única, a principal dos indicadores (atual, criados) ou a soma quando a operação soma
  const bandValueAt = (/** @type {number} */ index) => {
    if (plotted.length === 1 || drill?.widgetType === 'KPI') return plotted[0] ? valueAt(plotted[0], index) : 0;
    return drill?.isAdditive ? plotted.reduce((sum, series) => sum + valueAt(series, index), 0) : null;
  };
  const bandTargets = categories.map((category, index) => (drill ? drill.target({ part: 'category', key: category.key }, bandValueAt(index)) : null));

  /** @param {number} index @param {Element} element @param {BarHover['source']} source @param {string | null} [href] */
  const hoverAtElement = (index, element, source, href = null) => {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const box = element.getBoundingClientRect();
    // a dica sobe a partir do ponto: no meio da barra ela não sai do cartão nem cobre o eixo
    const y = Math.max(96, box.top + box.height / 2 - bounds.top) + (containerRef.current?.scrollTop ?? 0);
    setHover({ index, x: box.left + box.width / 2 - bounds.left, y, source, href });
  };
  const navigation = useChartPointNavigation({
    points: barPoints,
    label: drill?.widgetTitle ?? '',
    orientation: isHorizontal ? 'horizontal' : 'vertical',
    onFocusPoint: (point, element) => hoverAtElement(point.categoryIndex, element, 'keyboard'),
    onBlurPoint: () => setHover((current) => (current?.source === 'keyboard' ? null : current)),
    onPin: (point, event) => pinAt(point.categoryIndex, event, point.href),
    onUnpin: () => setHover(null),
  });

  /** @param {number} index @param {MouseEvent} event @param {string | null} href */
  function pinAt(index, event, href) {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setHover({ index, x: event.clientX - bounds.left, y: event.clientY - bounds.top + (containerRef.current?.scrollTop ?? 0), source: 'touch', href });
  }

  /**
   * Barra (com o link quando abre lista).
   * @param {number} categoryIndex @param {number} seriesIndex @param {any} shape
   */
  const withBarLink = (categoryIndex, seriesIndex, shape) => {
    const point = pointById.get(`${categoryIndex}:${seriesIndex}`);
    if (!point) return shape;
    return html`<a ...${navigation.pointProps(point, point)}>${shape}</a>`;
  };

  /**
   * Faixa da categoria, que recebe o ponteiro (e o clique, quando abre lista).
   * @param {number} categoryIndex @param {{ x: number, y: number, width: number, height: number }} rectangle
   */
  const renderCategoryBand = (categoryIndex, rectangle) => {
    const rect = html`<rect class="chart-hit-area" data-drillable=${bandTargets[categoryIndex] ? 'true' : undefined}
      x=${rectangle.x} y=${rectangle.y} width=${rectangle.width} height=${rectangle.height} />`;
    const target = bandTargets[categoryIndex];
    if (!target) return rect;
    return html`<${ChartBandLink} href=${target.href} label=${categories[categoryIndex].label} valueText=${formatMetricValue(target.value, format)}
      onTouchPin=${(/** @type {MouseEvent} */ event) => pinAt(categoryIndex, event, target.href)}>${rect}<//>`;
  };

  let content = null;
  if (width > 0 && size.height > 0 && categories.length > 0) {
    content = isHorizontal
      ? renderHorizontal()
      : renderVertical();
  }

  function renderVertical() {
    const height = size.height;
    const tickTarget = Math.max(2, Math.min(6, Math.floor(height / 45)));
    const scale = buildNiceScale(minimum, maximum, tickTarget, { integer });
    const tickLabels = scale.ticks.map((tick) => formatMetricValue(tick, format, { compact: true }));
    const left = Math.max(28, ...tickLabels.map((label) => estimateTextWidth(label) + 10));
    const right = 8;
    const plotWidth = Math.max(10, width - left - right);
    const band = plotWidth / categories.length;
    const maxLabelWidth = Math.max(...categories.map((category) => estimateTextWidth(category.label)));
    const rotate = maxLabelWidth > band - 6;
    const sine = Math.sin((LABEL_ROTATION_DEGREES * Math.PI) / 180);
    // rótulos girados nunca ocupam mais de um terço da altura: em cartões baixos eles encurtam
    const maxBottom = Math.max(30, Math.min(110, height * 0.34));
    const rotatedLabelWidth = Math.min(120, maxLabelWidth, (maxBottom - 22) / sine);
    const bottom = rotate ? Math.min(maxBottom, rotatedLabelWidth * sine + 22) : 22;
    const top = showDataLabels ? 18 : 8;
    const plotHeight = Math.max(10, height - top - bottom);
    const toY = (/** @type {number} */ value) => top + plotHeight * (1 - (value - scale.min) / (scale.max - scale.min || 1));
    const baseline = toY(0);
    const groupWidth = Math.min(band * 0.72, isStacked ? MAX_BAR_THICKNESS : MAX_BAR_THICKNESS * plotted.length);
    const barWidth = isStacked ? groupWidth : Math.max(2, (groupWidth - BAR_GAP * (plotted.length - 1)) / plotted.length);
    const labelStep = rotate ? Math.max(1, Math.ceil(14 / band)) : 1;
    const labelMaxWidth = rotate ? rotatedLabelWidth : band - 4;

    return html`<svg width=${width} height=${height} class="chart-svg" ...${navigation.containerProps}>
      ${hover !== null ? html`<rect class="chart-hover-band" x=${left + hover.index * band} y=${top} width=${band} height=${plotHeight} />` : null}
      ${scale.ticks.map((tick, index) => html`<g key=${`tick-${tick}`}>
        <line class=${tick === 0 ? 'chart-axis-line' : 'chart-grid-line'} x1=${left} x2=${left + plotWidth} y1=${toY(tick)} y2=${toY(tick)} />
        <text class="chart-axis-text" x=${left - 8} y=${toY(tick)} text-anchor="end" dominant-baseline="middle">${tickLabels[index]}</text>
      </g>`)}
      ${categories.map((category, categoryIndex) => {
        const groupX = left + categoryIndex * band + (band - groupWidth) / 2;
        let stackTop = 0;
        const bars = plotted.map((series, seriesIndex) => {
          const value = valueAt(series, categoryIndex);
          if (isStacked) {
            const start = stackTop;
            stackTop += Math.max(0, value);
            const y = toY(stackTop);
            const isTop = seriesIndex === plotted.length - 1 || plotted.slice(seriesIndex + 1).every((other) => valueAt(other, categoryIndex) <= 0);
            return html`<g key=${series.key}>${withBarLink(categoryIndex, seriesIndex, html`<path class="chart-bar chart-bar-stacked" fill=${colors[seriesIndex]}
              d=${buildBarPath(groupX, y, barWidth, toY(start) - y, isTop ? 'top' : 'none')} />`)}</g>`;
          }
          const x = groupX + seriesIndex * (barWidth + BAR_GAP);
          const y = value >= 0 ? toY(value) : baseline;
          const barHeight = Math.abs(toY(value) - baseline);
          return html`<g key=${series.key}>
            ${withBarLink(categoryIndex, seriesIndex, html`<path class="chart-bar" fill=${colors[seriesIndex]} d=${buildBarPath(x, y, barWidth, barHeight, value >= 0 ? 'top' : 'none')} />`)}
            ${showDataLabels && value !== 0 && barWidth + 8 >= estimateTextWidth(formatMetricValue(value, format, { compact: true }))
              ? html`<text class="chart-data-label" x=${x + barWidth / 2} y=${y - 5} text-anchor="middle">${formatMetricValue(value, format, { compact: true })}</text>`
              : null}
          </g>`;
        });
        const labelX = left + categoryIndex * band + band / 2;
        const labelY = top + plotHeight + 14;
        return html`<g key=${category.key}
          onPointerMove=${(/** @type {PointerEvent} */ event) => updateHover(categoryIndex, event)}
          onPointerLeave=${() => setHover((current) => (current?.source === 'touch' ? current : null))}>
          ${renderCategoryBand(categoryIndex, { x: left + categoryIndex * band, y: top, width: band, height: plotHeight })}
          ${bars}
          ${isStacked && showDataLabels && totals[categoryIndex] > 0
            ? html`<text class="chart-data-label" x=${groupX + groupWidth / 2} y=${toY(totals[categoryIndex]) - 5} text-anchor="middle">${formatMetricValue(totals[categoryIndex], format, { compact: true })}</text>`
            : null}
          ${categoryIndex % labelStep === 0
            ? rotate
              ? html`<text class="chart-axis-text" x=${labelX} y=${labelY - 4} text-anchor="end" transform=${`rotate(-${LABEL_ROTATION_DEGREES} ${labelX} ${labelY - 4})`}>${truncateToWidth(category.label, labelMaxWidth)}</text>`
              : html`<text class="chart-axis-text" x=${labelX} y=${labelY} text-anchor="middle">${truncateToWidth(category.label, labelMaxWidth)}</text>`
            : null}
        </g>`;
      })}
    </svg>`;
  }

  function renderHorizontal() {
    const rowHeight = 22;
    const tickTarget = Math.max(2, Math.min(6, Math.floor(width / 100)));
    const scale = buildNiceScale(minimum, maximum, tickTarget, { integer });
    const tickLabels = scale.ticks.map((tick) => formatMetricValue(tick, format, { compact: true }));
    const maxLabelWidth = Math.max(...categories.map((category) => estimateTextWidth(category.label)));
    const left = Math.min(width * 0.4, maxLabelWidth + 14);
    const valueLabels = categories.map((_, index) => formatMetricValue(isStacked ? totals[index] : Math.max(...plotted.map((series) => valueAt(series, index))), format, { compact: true }));
    const right = showDataLabels ? Math.max(12, ...valueLabels.map((label) => estimateTextWidth(label) + 10)) : 12;
    const bottom = 22;
    const top = 4;
    const height = Math.max(size.height, top + bottom + categories.length * rowHeight);
    const plotHeight = height - top - bottom;
    const plotWidth = Math.max(10, width - left - right);
    const band = plotHeight / categories.length;
    const toX = (/** @type {number} */ value) => left + plotWidth * ((value - scale.min) / (scale.max - scale.min || 1));
    const baseline = toX(0);
    const groupHeight = Math.min(band * 0.72, isStacked ? MAX_BAR_THICKNESS : MAX_BAR_THICKNESS * plotted.length);
    const barHeight = isStacked ? groupHeight : Math.max(2, (groupHeight - BAR_GAP * (plotted.length - 1)) / plotted.length);
    const tickStep = Math.max(1, Math.ceil(Math.max(...tickLabels.map((label) => estimateTextWidth(label) + 12)) / (plotWidth / Math.max(1, scale.ticks.length - 1))));

    return html`<svg width=${width} height=${height} class="chart-svg" ...${navigation.containerProps}>
      ${hover !== null ? html`<rect class="chart-hover-band" x=${left} y=${top + hover.index * band} width=${plotWidth} height=${band} />` : null}
      ${scale.ticks.map((tick, index) => html`<g key=${`tick-${tick}`}>
        <line class=${tick === 0 ? 'chart-axis-line' : 'chart-grid-line'} x1=${toX(tick)} x2=${toX(tick)} y1=${top} y2=${top + plotHeight} />
        ${index % tickStep === 0
          ? html`<text class="chart-axis-text" x=${toX(tick)} y=${top + plotHeight + 15} text-anchor="middle">${tickLabels[index]}</text>`
          : null}
      </g>`)}
      ${categories.map((category, categoryIndex) => {
        const groupY = top + categoryIndex * band + (band - groupHeight) / 2;
        let stackEnd = 0;
        return html`<g key=${category.key}
          onPointerMove=${(/** @type {PointerEvent} */ event) => updateHover(categoryIndex, event)}
          onPointerLeave=${() => setHover((current) => (current?.source === 'touch' ? current : null))}>
          ${renderCategoryBand(categoryIndex, { x: 0, y: top + categoryIndex * band, width, height: band })}
          <text class="chart-axis-text" x=${left - 8} y=${top + categoryIndex * band + band / 2} text-anchor="end" dominant-baseline="middle">${truncateToWidth(category.label, left - 12)}</text>
          ${plotted.map((series, seriesIndex) => {
            const value = valueAt(series, categoryIndex);
            if (isStacked) {
              const start = stackEnd;
              stackEnd += Math.max(0, value);
              const isEnd = plotted.slice(seriesIndex + 1).every((other) => valueAt(other, categoryIndex) <= 0);
              return html`<g key=${series.key}>${withBarLink(categoryIndex, seriesIndex, html`<path class="chart-bar chart-bar-stacked" fill=${colors[seriesIndex]}
                d=${buildBarPath(toX(start), groupY, toX(stackEnd) - toX(start), barHeight, isEnd ? 'right' : 'none')} />`)}</g>`;
            }
            const y = groupY + seriesIndex * (barHeight + BAR_GAP);
            const x = value >= 0 ? baseline : toX(value);
            return html`<g key=${series.key}>${withBarLink(categoryIndex, seriesIndex,
              html`<path class="chart-bar" fill=${colors[seriesIndex]} d=${buildBarPath(x, y, Math.abs(toX(value) - baseline), barHeight, value >= 0 ? 'right' : 'none')} />`)}</g>`;
          })}
          ${showDataLabels
            ? html`<text class="chart-data-label" x=${toX(isStacked ? totals[categoryIndex] : Math.max(0, ...plotted.map((series) => valueAt(series, categoryIndex)))) + 6}
                y=${top + categoryIndex * band + band / 2} dominant-baseline="middle">${valueLabels[categoryIndex]}</text>`
            : null}
        </g>`;
      })}
    </svg>`;
  }

  /**
   * @param {number} index
   * @param {PointerEvent} event
   */
  function updateHover(index, event) {
    // a dica fixada pelo toque só sai com um toque fora do gráfico
    if (event.pointerType === 'touch' || hover?.source === 'touch') return;
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setHover({ index, x: event.clientX - bounds.left, y: event.clientY - bounds.top + (containerRef.current?.scrollTop ?? 0), source: 'pointer' });
  }

  const hoverCategory = hover !== null ? categories[hover.index] : null;
  const hoverIsDrillable = hover !== null && (Boolean(bandTargets[hover.index]) || barPoints.some((point) => point.categoryIndex === hover.index));
  const pinnedHref = hover?.source === 'touch' ? hover.href ?? null : null;
  return html`<div class="chart">
    <div class="chart-plot" data-scrollable=${isHorizontal ? 'true' : undefined} ref=${containerRef}>
      ${content}
      ${hoverCategory && hover
        ? html`<${ChartTooltip}
            x=${hover.x}
            y=${hover.y - 10}
            containerWidth=${width}
            title=${hoverCategory.label}
            rows=${[
              ...plotted.map((series, index) => ({ key: series.key, label: series.label, value: formatMetricValue(series.values[hover.index], format), color: colors[index] })),
              ...(isStacked ? [{ key: '__total', label: t('dashboards.chart.total'), value: formatMetricValue(totals[hover.index], format) }] : []),
              ...extras.map((series) => ({ key: series.key, label: series.label, value: formatMetricValue(series.values[hover.index], series.format ?? format) })),
            ]}
            hint=${hoverIsDrillable ? (hover.source === 'keyboard' ? 'keyboard' : 'pointer') : null}
            action=${pinnedHref ? { href: pinnedHref, onClick: (/** @type {MouseEvent} */ event) => handleDrillLinkClick(event, pinnedHref) } : null}
          />`
        : null}
    </div>
    <${ChartLegend} items=${plotted.map((series, index) => ({ key: series.key, label: series.label, color: colors[index] }))} />
  </div>`;
}
